import React, { useCallback, useEffect, useState } from 'react';
import type { AgentRun } from '../../types/agent';
import { agentApi, apiErrorMessage } from '../../services/agentApi';
import { RunStatusChip, fmtDateTime, EmptyState, Spinner, card, input } from './agentUi';
import { useAuth } from '../../contexts/AuthContext';

const FILTERS: Array<{ key: string; label: string; status?: string }> = [
  { key: 'all', label: 'All' },
  { key: 'active', label: 'In progress', status: 'pending,running' },
  { key: 'approval', label: 'Needs approval', status: 'awaiting_approval' },
  { key: 'done', label: 'Completed', status: 'completed,completed_with_errors' },
  { key: 'failed', label: 'Failed', status: 'failed,cancelled' },
];

const AgentRunList: React.FC<{ onOpen: (runId: string) => void; refreshKey?: number }> = ({ onOpen, refreshKey }) => {
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';
  const [filter, setFilter] = useState('all');
  const [scope, setScope] = useState<'mine' | 'company'>(isAdmin ? 'company' : 'mine');
  const [runs, setRuns] = useState<AgentRun[] | null>(null);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [error, setError] = useState<string | null>(null);
  const [q, setQ] = useState('');

  const load = useCallback(async () => {
    try {
      const status = FILTERS.find((f) => f.key === filter)?.status;
      const r = await agentApi.runs({ status, scope, page });
      setRuns(r.runs);
      setTotal(r.total);
      setError(null);
    } catch (err) {
      setError(apiErrorMessage(err, 'Could not load runs'));
    }
  }, [filter, scope, page]);

  useEffect(() => {
    load();
    const t = setInterval(load, 15000);
    return () => clearInterval(t);
  }, [load, refreshKey]);

  const visible = (runs || []).filter((r) => {
    if (!q.trim()) return true;
    const hay = `${r.email?.subject || ''} ${r.email?.fromAddress || ''} ${r.instructions || ''} ${r.summary || ''}`.toLowerCase();
    return hay.includes(q.toLowerCase());
  });

  return (
    <div>
      <div className="flex flex-wrap items-center gap-2 mb-3">
        {FILTERS.map((f) => (
          <button
            key={f.key}
            onClick={() => { setFilter(f.key); setPage(1); }}
            className={`text-xs px-2.5 py-1 rounded-full border ${filter === f.key ? 'border-primary text-primary bg-primary/5' : 'border-gray-300 dark:border-gray-600 text-gray-600 dark:text-gray-300'}`}
          >
            {f.label}
          </button>
        ))}
        <div className="flex-1" />
        {isAdmin && (
          <select className={`${input} w-auto`} value={scope} onChange={(e) => { setScope(e.target.value as 'mine' | 'company'); setPage(1); }}>
            <option value="company">Company</option>
            <option value="mine">Mine</option>
          </select>
        )}
        <input className={`${input} w-56`} placeholder="Search subject, sender…" value={q} onChange={(e) => setQ(e.target.value)} />
      </div>
      {error && <p className="text-sm text-red-600 dark:text-red-400 mb-2">{error}</p>}
      {runs === null ? (
        <div className="flex items-center gap-2 text-gray-500 py-10 justify-center"><Spinner /> Loading…</div>
      ) : visible.length === 0 ? (
        <EmptyState title="No runs yet">Forward an email to the agent mailbox (see Settings), or start one with “Run Arie”.</EmptyState>
      ) : (
        <div className={`${card} divide-y divide-gray-100 dark:divide-gray-700`}>
          {visible.map((r) => (
            <button key={r.id} className="w-full text-left p-3 hover:bg-gray-50 dark:hover:bg-gray-800/60 flex items-start gap-3" onClick={() => onOpen(r.id)}>
              <span className="text-lg leading-none mt-0.5">{r.triggerType === 'email' ? '📧' : '🤖'}</span>
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-sm font-medium text-gray-900 dark:text-white truncate max-w-[36rem]">{r.email?.subject || r.instructions?.slice(0, 100) || 'Agent run'}</span>
                  <RunStatusChip status={r.status} mode={r.mode} />
                  {(r.pendingApprovals || 0) > 0 && <span className="text-xs text-amber-700 dark:text-amber-400">✋ {r.pendingApprovals}</span>}
                </div>
                <div className="text-xs text-gray-500 dark:text-gray-400 mt-0.5 truncate">
                  {r.email?.fromAddress ? `${r.email.fromAddress} · ` : ''}
                  {scope === 'company' && r.owner ? `${r.owner.firstName || r.owner.email} · ` : ''}
                  {fmtDateTime(r.createdAt)}
                  {r.artifactCount ? ` · ${r.artifactCount} artifact${r.artifactCount === 1 ? '' : 's'}` : ''}
                </div>
                {r.summary && <div className="text-xs text-gray-600 dark:text-gray-300 mt-1 line-clamp-2">{r.summary}</div>}
              </div>
            </button>
          ))}
        </div>
      )}
      {total > 50 && (
        <div className="flex justify-end gap-2 mt-3 text-sm">
          <button disabled={page === 1} onClick={() => setPage((p) => p - 1)} className="text-primary disabled:text-gray-400">Previous</button>
          <span className="text-gray-500">Page {page} of {Math.ceil(total / 50)}</span>
          <button disabled={page * 50 >= total} onClick={() => setPage((p) => p + 1)} className="text-primary disabled:text-gray-400">Next</button>
        </div>
      )}
    </div>
  );
};

export default AgentRunList;
